// Vinyl-bin (record-rack) browse engagement — modal kind. Aim-driven:
// hovering any sleeve in a bin engages browse mode on THAT bin. The
// focused sleeve at `browseIdx` (or the front sleeve when browseIdx = -1,
// i.e. not-yet-browsed) carries the outline + E-eligibility; arrow keys
// cycle the fan.
//
// browseIdx semantics:
//   -1   → "engaged but not yet browsed". Front sleeve is in focus
//          (outlined + E-targets it) but no sleeves tip forward.
//   ≥ 0  → fan is open; sleeve at browseIdx is in focus and forward-tipped.
// First arrow press transitions -1 → 0 (fan opens). ← from 0 collapses
// back to -1 (fan closes, front sleeve stays in focus).
//
// browseIdx is preserved across frames when aim stays on the same bin;
// reset to -1 when switching bins or first-engaging.

import { registerInteractable } from "../_interactable-registry.js";
import { addOutline, removeOutline } from "../helpers.js";

registerInteractable({
	id: "vinyl-bin",
	precedence: 100,
	// ASK-137 §84 — THIS KIND'S OWN PROMPT AND SUBJECT. `stage.js` used to enumerate every kind by
	// hand, twice (`_updateEHint`'s hover ladder and `_hoveredProduct`), which is exactly what the
	// registry's own header says a registry exists to end. The prompt a shopper reads and the product
	// it names belong where the rest of this kind's behaviour already is.
	hoverHint: '<kbd>E</kbd> Inspect &nbsp;·&nbsp; <kbd>←→</kbd> Browse',
	productOf(stage, active) {
		const b = stage.activeVinylBin; if (!b) return active?.product ?? null;
		const i = Math.max(0, b.browseIdx); return b.sleeves[i]?.product ?? null;
	},

	suppressedBy(stage) {
		if (stage.boxState === "open" || stage.boxState === "browsing" || stage.boxState === "hero") return true;
		if (stage.activeFixtureHero) return true;
		if (!stage.vinylSleeves?.length) return true;
		return false;
	},

	// Raycasts against the SHARED vinyl-sleeves pool (not just bin sleeves)
	// so the closest-overall hit determines the winner — matches legacy
	// behaviour where a single raycast across all sleeves was discriminated
	// by `fixtureKind`. Without this, a more-distant bin sleeve could win
	// over a closer wall-shelf sleeve when both are in the line of sight.
	// `onHit` then bails if the closest hit isn't a bin sleeve.
	getTargets(stage) {
		return stage.vinylSleeves.map((v) => v.mesh);
	},

	// Returns the (fixtureIndex, sleeves, browseIdx) tuple — rebuilt each
	// frame so commitHover always sees a reference change (lets the
	// dispatcher pick up intra-active changes like browseIdx mutating).
	onHit(stage, mesh) {
		if (mesh.userData?.interactable?.fixtureKind !== "vinyl-bin") return null;
		const binIdx = mesh.userData.interactable.fixtureIndex;
		if (binIdx == null) return null;
		const sleeves = stage.vinylSleeves.filter((v) => {
			const i = v.mesh.userData?.interactable;
			return i?.fixtureKind === "vinyl-bin" && i.fixtureIndex === binIdx;
		});
		const browseIdx = (stage.activeVinylBin?.fixtureIndex === binIdx)
			? stage.activeVinylBin.browseIdx
			: -1;
		return { fixtureIndex: binIdx, browseIdx, sleeves };
	},

	commitHover(stage, prev, next) {
		stage.activeVinylBin = next;

		// Outline target: focused sleeve at max(0, browseIdx) — the front
		// sleeve stays focused even when browseIdx is the -1 sentinel.
		const focusedMesh = next
			? (next.sleeves[Math.max(0, next.browseIdx)]?.mesh ?? null)
			: null;
		if (focusedMesh === stage.hoveredVinyl) return;
		if (stage.hoveredVinyl) removeOutline(stage.hoveredVinyl);
		stage.hoveredVinyl = focusedMesh;
		if (focusedMesh) addOutline(focusedMesh);
	},

	interactE(stage) {
		if (!stage.hoveredVinyl) return false;
		if (stage.boxState === "closed") stage.forceCloseActiveBox();
		stage.inspectVinyl(stage.hoveredVinyl.userData.vinylIdx);
		return true;
	},
});
